import { Children } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Divider from "@amzn/meridian/esm/components/divider";
import Link from "@amzn/meridian/esm/components/link";
import Table, { TableCell, TableRow } from "@amzn/meridian/esm/components/table";
import Tile from "@amzn/meridian/esm/components/tile";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import { openUrl } from "../../../utils/zoomSdkUtils";
import { THEME_COLOR } from "../../../styles/colors";

const openLink = (href) => openUrl(href);

const MdTableRow = ({ children }) => <TableRow highlightOnHover>{children}</TableRow>;
MdTableRow.WrappedComponent = TableRow;

// `breakWord` on every block-level Text: the assistant renders into a 318px panel, so
// an unbroken run of characters (a long URL, id, or hash) would otherwise flow past
// the right edge. overflow-wrap inherits, so inline tags like strong/em are covered
// by their containing block.
const markdownComponents = {
  p: ({ children }) => <Text type="b200" breakWord>{children}</Text>,
  strong: ({ children }) => <strong>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  h1: ({ children }) => <Text type="h200" breakWord>{children}</Text>,
  h2: ({ children }) => <Text type="h100" breakWord>{children}</Text>,
  h3: ({ children }) => <Text type="h50" breakWord>{children}</Text>,
  h4: ({ children }) => <Text type="h50" breakWord>{children}</Text>,
  h5: ({ children }) => <Text type="h50" breakWord>{children}</Text>,
  h6: ({ children }) => <Text type="h50" breakWord>{children}</Text>,
  del: ({ children }) => <s>{children}</s>,
  ul: ({ children }) => <Text type="b200" tag="ul" listStyle="disc" breakWord>{children}</Text>,
  ol: ({ children }) => <Text type="b200" tag="ol" breakWord>{children}</Text>,
  li: ({ children }) => <Text type="b200" tag="li" breakWord>{children}</Text>,
  a: ({ href, children }) => <Link href={href} onClick={() => openLink(href)}>{children}</Link>,
  code: ({ className, children, ...props }) => (
    className
      ? <code className={className} {...props}>{children}</code>
      : <Theme tokens={{ boxBorderRadiusFill: 4 }}>
          <Box tag="code" type="fill" backgroundColor={THEME_COLOR.MIST_GRAY} spacingInset="0 100">
            <Text type="b100" tag="span" breakWord>{children}</Text>
          </Box>
        </Theme>
  ),
  table: ({ children }) => {
    const rows = Children.toArray(children).flatMap((child) =>
      child?.props?.children ? Children.toArray(child.props.children) : [child]
    );
    return (
      <Theme tokens={{ tableBodyFontSize: 12, tableBodyLineHeight: "16px", tableTableHeaderFontSize: 12, tableTableHeaderLineHeight: "16px", tableSpacingHorizontal: 8 }}>
        <Table headerRows={1} showDividers spacing="small" layout="auto" rowComponents={[MdTableRow]}>{rows}</Table>
      </Theme>
    );
  },
  thead: ({ children }) => children,
  tbody: ({ children }) => children,
  tr: MdTableRow,
  th: ({ children }) => <TableCell>{children}</TableCell>,
  td: ({ children }) => <TableCell>{children}</TableCell>,
  blockquote: ({ children }) => (
    <Theme tokens={{ tileBorderWidth: 0, tileAccentColorNeutral: THEME_COLOR.TEAL_GREEN }}>
      <Tile accent="neutral" accentPosition="start" spacingInset="none">
        <Box spacingInset="200 300">
          <Text type="b200" tag="div" color="secondary" breakWord>{children}</Text>
        </Box>
      </Tile>
    </Theme>
  ),
  // Code blocks scroll instead of wrapping: breaking a long line mid-token would
  // misrepresent the code. Needs a real `pre` — its `white-space: pre` is what keeps
  // lines intact so there's something for overflowX to scroll.
  pre: ({ children }) => (
    <Theme tokens={{ boxBorderRadiusFill: 12 }}>
      <Box type="fill" backgroundColor={THEME_COLOR.MIST_GRAY} spacingInset="300" overflowX="auto">
        <Text type="b100" tag="pre">{children}</Text>
      </Box>
    </Theme>
  ),
  hr: () => <Divider />,
};

export default markdownComponents;
